/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import {
	FormProvider,
	useController,
	useForm,
	useFormState
} from "react-hook-form";
import { ArrowLeft, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import isEmpty from "lodash.isempty";
import { KeyShoutFormValues } from "../../adapters/formValuesToKeyShout";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import GroupsAudienceCard from "src/components/GroupsAudienceCard/GroupsAudienceCard";
import LinkPickerStep from "src/components/LinkPicker/LinkPickerStep";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";

export type { KeyShoutFormValues };

interface Props {
	defaultValues: KeyShoutFormValues;
	onSubmit: (data: KeyShoutFormValues, imageRemoved: boolean) => void;
	isSubmitting?: boolean;
	isEdit?: boolean;
	onDelete?: () => void;
	existingImageReference?: string | null;
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_SIZE = 2_097_152; // 2 MB

const KeyShoutForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	isSubmitting = false,
	isEdit = false,
	onDelete,
	existingImageReference
}) => {
	const methods = useForm<KeyShoutFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch, setValue } = methods;
	const { isDirty, errors } = useFormState({ control });

	const { field: audienceGroupIds } = useController({
		control,
		name: "audienceGroupIds",
		defaultValue: []
	});
	const {
		data: adminGroups = [],
		isLoading: groupsLoading,
		isError: groupsError
	} = useAdminGroups();

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [submitAttempted, setSubmitAttempted] = useState(false);

	const imageReference = watch("image");

	const {
		upload,
		uploading: isUploading,
		error: uploadError
	} = useAzureFileUpload((data) => {
		if (Array.isArray(data) && typeof data[0] === "string") {
			setValue("image", data[0], { shouldDirty: true });
		}
	});

	useEffect(() => {
		if (uploadError) setImageError("Failed to upload image, try again.");
	}, [uploadError]);

	const hasImage =
		!imageRemoved &&
		(!!imageReference || !!previewBlobUrl || !!existingImageReference);
	const showImageRequiredError = !hasImage && (submitAttempted || imageRemoved);

	const isSubmitBtnDisabled =
		(!isDirty && !imageRemoved) ||
		!isEmpty(errors) ||
		isSubmitting ||
		isUploading ||
		!!imageError;

	useEffect(() => {
		reset(defaultValues);
		setImageRemoved(false);
		setImageError(null);
		setSubmitAttempted(false);
		setPreviewBlobUrl((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});
	}, [defaultValues]);

	// Drop the local blob preview once the upload returns a reference — AzureImage
	// takes over from there.
	useEffect(() => {
		if (imageReference && previewBlobUrl) {
			URL.revokeObjectURL(previewBlobUrl);
			setPreviewBlobUrl(null);
		}
	}, [imageReference, previewBlobUrl]);

	const validateAndSetFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setImageError("Image must be under 2 MB.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setImageError(null);
		if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
		setPreviewBlobUrl(URL.createObjectURL(file));
		setImageRemoved(false);
		upload(file, "images", undefined, "admin-content-image");
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) validateAndSetFile(file);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) validateAndSetFile(file);
	};

	const handleRemoveImage = () => {
		setValue("image", null, { shouldDirty: true });
		if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
		setPreviewBlobUrl(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const renderImagePreview = () => {
		if (previewBlobUrl) {
			return (
				<img
					src={previewBlobUrl}
					alt="Uploaded image preview"
					className="object-contain w-full h-auto max-h-[400px]"
				/>
			);
		}
		const ref = imageReference ?? existingImageReference;
		if (ref) {
			return (
				<AzureImage
					uuid={ref}
					imageProfile="admin-content-image"
					alt="Uploaded image preview"
					className="object-contain w-full h-auto max-h-[400px]"
				/>
			);
		}
		return null;
	};

	return (
		<form
			onSubmit={(e) => {
				setSubmitAttempted(true);
				handleSubmit((data) => {
					if (!hasImage) return;
					onSubmit(data, imageRemoved);
				})(e);
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Heading */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.heading ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.heading && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please fill out the required field
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Heading text
							</label>
							<FormField
								control={control}
								name="heading"
								rules={{
									required: "Heading is required",
									maxLength: {
										value: 40,
										message: "Maximum 40 characters"
									}
								}}
								render={({ field }) => (
									<FormItem className="space-y-0">
										<FormControl>
											<Input
												placeholder="Maximum heading count is 40 characters."
												maxLength={40}
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<p className="text-[13px] text-zinc-600 text-right mt-1">
								{watch("heading")?.length || 0} / 40
							</p>
						</div>
					</div>

					{/* Content */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Content text
							</label>
							<FormField
								control={control}
								name="content"
								rules={{
									maxLength: {
										value: 80,
										message: "Maximum 80 characters"
									}
								}}
								render={({ field }) => (
									<FormItem className="space-y-0">
										<FormControl>
											<Input
												placeholder="Maximum content count is 80 characters."
												maxLength={80}
												className="w-full bg-white"
												{...field}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<p className="text-[13px] text-zinc-600 text-right mt-1">
								{watch("content")?.length || 0} / 80
							</p>
						</div>
					</div>

					{/* Image */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							showImageRequiredError ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{showImageRequiredError && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									An image is required
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Add image
							</label>
							<p className="text-[13px] text-muted-foreground mb-4">
								jpg or png, 250 x 250px
							</p>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/jpg,image/png"
								className="hidden"
								onChange={handleFileChange}
							/>
							<div className="max-w-[480px]">
								{hasImage ? (
									<>
										<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
											{renderImagePreview()}
										</div>
										{isUploading && (
											<p className="text-xs text-muted-foreground mb-2">
												Uploading…
											</p>
										)}
										<div className="flex items-center gap-2">
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
												onClick={() => fileInputRef.current?.click()}
											>
												Change image
											</button>
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
												onClick={handleRemoveImage}
											>
												Remove
											</button>
										</div>
									</>
								) : (
									<div
										role="button"
										tabIndex={0}
										onClick={() => fileInputRef.current?.click()}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												fileInputRef.current?.click();
											}
										}}
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										onDrop={handleDrop}
										className={[
											"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
											isDragging
												? "border-primary bg-primary-lighter"
												: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50"
										].join(" ")}
									>
										<Upload className="h-6 w-6 text-muted-foreground" />
										<div>
											<p className="text-sm font-medium text-foreground">
												Drag image here or click to browse
											</p>
											<p className="text-xs text-muted-foreground mt-1">
												JPG or PNG
											</p>
										</div>
									</div>
								)}
							</div>
							{imageError && (
								<p className="text-sm text-red-500 mt-2">{imageError}</p>
							)}
						</div>
					</div>

					{/* Link (optional) */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Link (optional)
							</label>
							<LinkPickerStep
								urlName="buttonLink"
								webviewName="webview"
								feedName="feedId"
								selectedFeedName="selectedFeed"
								label="Link (optional)"
								helperText="Choose one. Switching options replaces your current selection."
								hideLabel
							/>
						</div>
					</div>

					{/* Groups audience */}
					<div className="mt-4">
						<GroupsAudienceCard
							groupIds={audienceGroupIds.value ?? []}
							onChange={audienceGroupIds.onChange}
							groups={adminGroups.map((g) => ({
								id: Number(g.id),
								name: g.name
							}))}
							isLoading={groupsLoading}
							isError={groupsError}
						/>
					</div>
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to="/app/homepage/key-shouts"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to key shouts
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<Button type="submit" disabled={isSubmitBtnDisabled}>
						{isSubmitting ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							"Save"
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default KeyShoutForm;
